/**
 * Presenting a location's work rollup.
 *
 * The server's `siteWorkRollup` decides WHAT is true about a location; this
 * file decides how to say it. Every function here exists to stop the page
 * printing a number that reads as good news when it is really an absence of
 * information — the single failure the audit found over and over:
 *
 *   - "0 devices" looks identical to "0 overdue devices". One is compliant,
 *     the other means we have never surveyed the address.
 *   - A blank Last-visit cell looks like a rendering gap. It means never.
 *   - "—" under Money looks like $0 owed. It might mean we did not check.
 *
 * Doctrine anchor: product-doctrine.md, "Never fake precision."
 */

export interface SiteRollup {
    site_id: string;
    device_count: number;
    overdue_count: number;
    due_90_count: number;
    missing_due_count: number;
    oldest_overdue_date: string | null;
    next_due_date: string | null;
    last_test_at: string | null;
    last_visited_at: string | null;
    open_job_count: number;
    scheduled_job_count: number;
    due_soon_unscheduled_count: number;
    open_estimate_count: number;
    open_estimate_value: string;
    approved_unscheduled_count: number;
    approved_unscheduled_value: string;
    unpaid_invoice_count: number;
    unpaid_balance: string;
    portal_account_missing: boolean;
    never_visited: boolean;
    imported_only: boolean;
}

export type ComplianceTone = "overdue" | "current" | "unsurveyed" | "no_devices";

export interface CompliancePill {
    tone: ComplianceTone;
    label: string;
    /** Second line — the age of the problem, or why there is no answer. */
    detail: string | null;
}

/**
 * One pill per location. The two grey states are the point: "Never surveyed"
 * and "No devices on record" are DIFFERENT from each other and neither is
 * "Current". A site with no devices has no compliance state at all, and
 * rendering it green would be a lie the office acts on.
 */
export function compliancePill(r: SiteRollup | null | undefined): CompliancePill {
    if (!r) return { tone: "unsurveyed", label: "Not loaded", detail: null };
    if (r.device_count === 0) {
        return {
            tone: "no_devices",
            label: "No devices on record",
            detail: r.never_visited ? "Never worked under this system" : "We hold no device list for this address",
        };
    }
    if (r.overdue_count > 0) {
        const age = daysSince(r.oldest_overdue_date);
        return {
            tone: "overdue",
            label: `${r.overdue_count} overdue`,
            detail: age === null ? null : `Oldest ${age} ${age === 1 ? "day" : "days"} past due`,
        };
    }
    if (r.next_due_date) {
        return {
            tone: "current",
            label: `Current to ${formatShortDate(r.next_due_date)}`,
            detail: r.due_90_count > 0 ? `${r.due_90_count} due within 90 days` : null,
        };
    }
    // Devices exist but not one of them carries a due date, so this location
    // is invisible in every due queue. That is worse than overdue, not better.
    return {
        tone: "unsurveyed",
        label: "Never surveyed",
        detail: `${r.device_count} ${r.device_count === 1 ? "device" : "devices"} with no due date`,
    };
}

/** Devices cell. Zero is words, not a digit — "0" reads as a measurement. */
export function devicesLabel(r: SiteRollup | null | undefined): string {
    if (!r) return "—";
    if (r.device_count === 0) return "none on record";
    return `${r.device_count}`;
}

export interface LastVisitLabel {
    text: string;
    /** Narrows the claim; ServiceTitan history may exist for the address. */
    qualifier: string | null;
    never: boolean;
}

export function lastVisitLabel(r: SiteRollup | null | undefined): LastVisitLabel {
    const raw = r?.last_visited_at;
    if (!raw) return { text: "Never", qualifier: "under this system", never: true };
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return { text: "Never", qualifier: "under this system", never: true };
    return { text: formatShortDate(raw), qualifier: null, never: false };
}

export interface OpenWorkLabel {
    text: string;
    /** True when there is genuinely nothing on the board. */
    idle: boolean;
    /** Named alarm: a hard deadline with nothing scheduled against it. */
    warning: string | null;
}

export function openWorkLabel(r: SiteRollup | null | undefined): OpenWorkLabel {
    const warning =
        r && r.due_soon_unscheduled_count > 0 ? `${r.due_soon_unscheduled_count} due soon, not scheduled` : null;
    if (!r || r.open_job_count === 0) return { text: "Nothing scheduled", idle: true, warning };
    if (r.scheduled_job_count > 0) {
        return {
            text: `${r.scheduled_job_count} scheduled${
                r.open_job_count > r.scheduled_job_count ? ` · ${r.open_job_count - r.scheduled_job_count} not yet` : ""
            }`,
            idle: false,
            warning,
        };
    }
    return { text: `${r.open_job_count} open, none on the board`, idle: false, warning };
}

export interface MoneyLabel {
    owed: string | null;
    approved: string | null;
    /** Nothing owed and nothing quoted — say so rather than showing a dash. */
    empty: boolean;
}

export function moneyLabel(r: SiteRollup | null | undefined): MoneyLabel {
    const owed = r ? Number(r.unpaid_balance) : 0;
    const approved = r ? Number(r.approved_unscheduled_value) : 0;
    return {
        owed: owed > 0.005 ? formatMoney(owed) : null,
        approved: approved > 0.005 ? formatMoney(approved) : null,
        empty: !(owed > 0.005 || approved > 0.005),
    };
}

export interface FilingLabel {
    blocked: boolean;
    text: string;
}

export function filingLabel(
    r: SiteRollup | null | undefined,
    site: { water_provider_name?: string | null; water_provider_submission_platform?: string | null },
): FilingLabel {
    if (r?.portal_account_missing) return { blocked: true, text: "acct # missing" };
    if (!site.water_provider_name) return { blocked: false, text: "No purveyor set" };
    return { blocked: false, text: site.water_provider_submission_platform ?? site.water_provider_name };
}

// ── formatting ───────────────────────────────────────────────────────────────

export function formatMoney(value: number): string {
    return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function formatShortDate(value: string): string {
    const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function daysSince(value: string | null): number | null {
    if (!value) return null;
    const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((today.getTime() - d.getTime()) / 86_400_000));
}
